import { supabase } from "@/integrations/supabase/client";

export interface FCAValidationResult {
  isValid: boolean;
  firmName?: string;
  tradingNames?: string[];
  status?: string;
  frn?: string;
  error?: string;
}

export interface FCAFirmNameMatch {
  isSimilar: boolean;
  similarity: number; // 0-100
  shouldWarn: boolean;
}

/**
 * Validate FCA number format (must be exactly 6 digits)
 */
export function isValidFCANumberFormat(fcaNumber: string): boolean {
  return /^\d{6}$/.test(fcaNumber);
}

/**
 * Validate FCA number against the FCA register API
 */
export async function validateFCANumber(fcaNumber: string): Promise<FCAValidationResult> {
  if (!isValidFCANumberFormat(fcaNumber)) {
    return {
      isValid: false,
      error: "FCA number must be exactly 6 digits"
    };
  }

  try {
    console.log('[FCA Validation] ==========================================');
    console.log('[FCA Validation] Starting validation for FCA number:', fcaNumber);
    
    const { data, error } = await supabase.functions.invoke('validate-fca', {
      body: { fcaNumber }
    });

    console.log('[FCA Validation] Edge function response received:');
    console.log('[FCA Validation] - Error:', error);
    console.log('[FCA Validation] - Data:', JSON.stringify(data, null, 2));

    if (error) {
      console.error('[FCA Validation] Edge function returned an error:', error);
      throw error;
    }

    // CRITICAL: Only return true if data explicitly says isValid: true AND has firmName
    if (!data) {
      console.error('[FCA Validation] No data returned from edge function');
      return {
        isValid: false,
        error: 'No response from FCA validation service'
      };
    }

    console.log('[FCA Validation] Checking validation criteria:');
    console.log('[FCA Validation] - data.isValid:', data.isValid, '(type:', typeof data.isValid, ')');
    console.log('[FCA Validation] - data.firmName:', data.firmName);
    console.log('[FCA Validation] - data.tradingNames:', data.tradingNames);
    console.log('[FCA Validation] - data.status:', data.status);
    console.log('[FCA Validation] - data.frn:', data.frn);
    console.log('[FCA Validation] - data.error:', data.error);

    if (data.isValid !== true) {
      console.log('[FCA Validation] REJECTED: isValid is not exactly true');
      return {
        isValid: false,
        error: data.error || 'FCA number could not be verified'
      };
    }

    if (!data.firmName || data.firmName.trim() === '') {
      console.log('[FCA Validation] REJECTED: No firm name in response');
      return {
        isValid: false,
        error: 'FCA number verified but no firm name found'
      };
    }

    console.log('[FCA Validation] ✓ SUCCESS! Firm:', data.firmName);
    console.log('[FCA Validation] - Trading names available:', data.tradingNames?.length || 0);
    console.log('[FCA Validation] ==========================================');
    return data;
  } catch (error: any) {
    console.error('[FCA Validation] ==========================================');
    console.error('[FCA Validation] Exception caught during validation:');
    console.error('[FCA Validation] - Message:', error.message);
    console.error('[FCA Validation] - Full error:', error);
    console.error('[FCA Validation] ==========================================');
    return {
      isValid: false,
      error: error.message || 'Failed to validate FCA number - please try again'
    };
  }
}

/**
 * Calculate Levenshtein distance between two strings
 */
function levenshteinDistance(str1: string, str2: string): number {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();
  const len1 = s1.length;
  const len2 = s2.length;
  const matrix: number[][] = [];

  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      const cost = s1[i - 1] === s2[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }

  return matrix[len1][len2];
}

/**
 * Calculate similarity percentage between two strings
 */
function calculateSimilarity(str1: string, str2: string): number {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);
  if (maxLength === 0) return 100;
  return Math.round((1 - distance / maxLength) * 100);
}

/**
 * Compare user-entered firm name with FCA register name
 */
export function compareFirmNames(userEnteredName: string, fcaRegisterName: string): FCAFirmNameMatch {
  const similarity = calculateSimilarity(userEnteredName, fcaRegisterName);
  
  return {
    isSimilar: similarity >= 70,
    similarity,
    shouldWarn: similarity < 70
  };
}

/**
 * Format FCA number for display (e.g., "123456" -> "123456")
 */
export function formatFCANumber(fcaNumber: string): string {
  return fcaNumber.replace(/\D/g, '').slice(0, 6);
}
